import { useCallback, useMemo, useState } from "react"

import {
  selectMinimumNetDebt,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import {
  GAS_BUFFER_FOR_BORROW_TRANSACTION,
  useBorrowForm,
  useMaxLoanAmount,
  useMinimumCollateralRatio,
  useMinimumRequiredCollateralForNewLoan,
} from "#/shared/hooks"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  getCurrencySymbol,
  separateThousandsWithComma,
} from "#/shared/utils"
import { AmountInput } from "#/ui/Input"

export default function BorrowInputs() {
  const {
    ratio,
    loanAmount,
    onMaxLoanAmount,
    collateralAmount,
    onRatioChange,
    setCollateralBasedOnRatio,
    onLoanAmountChange,
    onCollateralAmountChange,
    isRatioValid,
    isLoanAmountValid,
    isCollateralAmountValid,
  } = useBorrowForm()

  const { mBTC, MUSD } = useDappSelector(selectWalletBalances)
  const { maxCollateralBalance } = useMaxLoanAmount()

  const { requiredCollateralAmount } = useMinimumRequiredCollateralForNewLoan()

  const { minimumCollateralRatioLabel } = useMinimumCollateralRatio()

  const [showMaxCollateralInfo, setShowMaxCollateralInfo] = useState(false)
  const minimumLoanAmount = useDappSelector(selectMinimumNetDebt)

  const collateralBalance = useMemo(
    () =>
      bigIntToHumanReadableFormat(
        mBTC.balance,
        Number(mBTC.decimals),
        Number(mBTC.decimals),
      ),
    [mBTC.balance, mBTC.decimals],
  )

  const ratioInputValue = useMemo(() => {
    if (!ratio) {
      return ""
    }

    return `${ratio}`
  }, [ratio])

  const loanAmountMessage = useMemo(() => {
    if (!minimumLoanAmount || Number.isNaN(loanAmount)) {
      return ""
    }

    const minimumLoanAmountLabel = bigIntToFloat(
      minimumLoanAmount,
      Number(MUSD.decimals),
      2,
    )

    if (floatToBigInt(loanAmount) < minimumLoanAmount) {
      return `Minimum amount is ${minimumLoanAmountLabel} MUSD`
    }

    return ""
  }, [loanAmount, MUSD.decimals, minimumLoanAmount])

  const handleMaxCollateralClick = useCallback(() => {
    onMaxLoanAmount()
    setShowMaxCollateralInfo(true)
  }, [onMaxLoanAmount])

  const handleCollateralChange = useCallback(
    (value: string) => {
      onCollateralAmountChange(value)
      setShowMaxCollateralInfo(false)
    },
    [onCollateralAmountChange],
  )

  const gasBufferAmount = useMemo(
    () =>
      bigIntToHumanReadableFormat(
        GAS_BUFFER_FOR_BORROW_TRANSACTION,
        Number(mBTC.decimals),
        Number(mBTC.decimals),
      ),
    [mBTC.decimals],
  )

  const collateralRatioMessage = useMemo(
    () =>
      `Minimum ${minimumCollateralRatioLabel}% - To minimize liquidation risk, consider collateralizing 151%+ to create a buffer against price fluctuations.`,
    [minimumCollateralRatioLabel],
  )

  const collateralBalanceMessage = `Adjusted to reserve ${gasBufferAmount} ${getCurrencySymbol(
    "mBTC",
  )} for network fee.`

  const collateralErrorMessage = useMemo(() => {
    if (floatToBigInt(collateralAmount) > mBTC.balance)
      return "Insufficient funds. Try lower amount."

    if (floatToBigInt(collateralAmount) > maxCollateralBalance)
      return "Insufficient balance for network fees."

    return "Not enough collateral"
  }, [collateralAmount, mBTC.balance, maxCollateralBalance])

  const hasBalanceError = floatToBigInt(collateralAmount) > mBTC.balance

  return (
    <>
      <AmountInput
        label="Borrow amount"
        token="MUSD"
        placeholder="0 MUSD"
        unitLabel=" MUSD"
        value={loanAmount}
        balanceUsd={separateThousandsWithComma(loanAmount) || "0.00"}
        onChange={(ev) => onLoanAmountChange(ev.target.value)}
        onBlur={() => setCollateralBasedOnRatio()}
        error={loanAmount ? !isLoanAmountValid : false}
        errorMessage={loanAmountMessage}
      />
      <AmountInput
        label="Collateralization ratio"
        placeholder="0%"
        value={ratioInputValue}
        infoMessage={collateralRatioMessage}
        errorMessage={collateralRatioMessage}
        onChange={(ev) => onRatioChange(ev.target.value)}
        onBlur={() => setCollateralBasedOnRatio()}
        error={ratioInputValue ? !isRatioValid : false}
        unitLabel="%"
        noUnitLabelGap
      />
      <AmountInput
        label="Required collateral amount"
        token="mBTC"
        placeholder={`0 ${getCurrencySymbol("mBTC")}`}
        unitLabel={` ${getCurrencySymbol("mBTC")}`}
        value={collateralAmount}
        balanceToken={collateralBalance}
        hasBalanceError={hasBalanceError}
        onButtonMax={handleMaxCollateralClick}
        isMaxDisabled={mBTC.balance < requiredCollateralAmount}
        onChange={(ev) => handleCollateralChange(ev.target.value)}
        error={collateralAmount ? !isCollateralAmountValid : false}
        errorMessage={collateralErrorMessage}
        infoMessage={showMaxCollateralInfo ? collateralBalanceMessage : ""}
        isInfoMessageHighlighted
      />
    </>
  )
}
